import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, forkJoin, Observable, Subject, takeUntil, tap } from 'rxjs';
import { FORM_SHARED_IMPORTS } from 'src/app/shared/imports/form-shared.imports';
import { CxSelectComponent } from 'src/app/shared/components/cx-select/cx-select.component';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { CountryCallingCodeOptionDto } from 'src/core/models/settings/localization/country-calling-code-option.dto';
import { DateDisplayFormat } from 'src/core/models/settings/localization/date-display-format.enum';
import {
  DATE_FORMAT_OPTIONS,
  LocalizationFormatOption,
  NUMBER_FORMAT_OPTIONS,
  PHONE_NUMBER_FORMAT_OPTIONS,
  TIME_FORMAT_OPTIONS,
} from 'src/core/models/settings/localization/localization-format-options.constant';
import {
  LanguageOptionDto,
  TimeZoneOptionDto,
} from 'src/core/models/settings/localization/localization-settings-options.dto';
import { LocalizationSettingsDto } from 'src/core/models/settings/localization/localization-settings.dto';
import { NumberDisplayFormat } from 'src/core/models/settings/localization/number-display-format.enum';
import { PhoneNumberDisplayFormat } from 'src/core/models/settings/localization/phone-number-display-format.enum';
import { TimeDisplayFormat } from 'src/core/models/settings/localization/time-display-format.enum';
import { LocalizationSettingsService } from 'src/core/services/settings/localization-settings.service';

/** Dropdown option after localization is applied. */
interface SelectOption<TValue> {
  value: TValue;
  label: string;
}

/** Default country code option; `searchText` keeps every region name searchable when the label is shortened. */
interface CountryCodeSelectOption extends SelectOption<string> {
  searchText: string;
}

/**
 * Region names shown in a country code label. `+1` alone covers 25 regions, and the dropdown overlay grows
 * to its widest option, so longer lists are cut and end with an ellipsis.
 */
const MAX_LABELLED_REGIONS = 3;

/**
 * Which group of preferences a rendered instance shows. The Localization tab splits them over two
 * sub-tabs, and each sub-tab saves the whole resource — the fields it does not show simply keep the
 * values that were loaded.
 */
export type LocalizationSection = 'formats' | 'language';

/** Region name formatter of one UI language; `null` when the runtime cannot build one. */
interface RegionNameFormatter {
  language: string;
  formatter: Intl.DisplayNames | null;
}

/**
 * Administration > Settings > Localization. Only the time zone reaches server side calculations;
 * the remaining fields are presentation only.
 */
@Component({
  selector: 'app-localization-settings',
  standalone: true,
  templateUrl: './localization-settings.component.html',
  styleUrl: './localization-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...FORM_SHARED_IMPORTS, LocalizationPipe, CxSelectComponent],
})
export class LocalizationSettingsComponent
  extends BaseFormComponent<LocalizationSettingsDto>
  implements OnDestroy
{
  private readonly localizationSettingsService = inject(LocalizationSettingsService);
  private readonly localization = inject(LocalizationService);
  private readonly destroy$ = new Subject<void>();

  dateFormatOptions: SelectOption<DateDisplayFormat>[] = [];
  timeFormatOptions: SelectOption<TimeDisplayFormat>[] = [];
  phoneNumberFormatOptions: SelectOption<PhoneNumberDisplayFormat>[] = [];
  numberFormatOptions: SelectOption<NumberDisplayFormat>[] = [];
  languageOptions: LanguageOptionDto[] = [];
  timeZoneOptions: TimeZoneOptionDto[] = [];
  /** Default country code dropdown: the codes the server accepts, localized once per options load. */
  countryCodeOptions: CountryCodeSelectOption[] = [];

  /** Preference groups to render. Defaults to all of them so the component stays usable on its own. */
  @Input() sections: readonly LocalizationSection[] = ['formats', 'language'];

  isLoading = true;
  isLoadingOptions = true;

  /** Last values known to be persisted; Cancel restores exactly these. */
  private savedValue: LocalizationSettingsDto | null = null;

  /** Built at most once per UI language, never per option or per change detection. */
  private regionNames: RegionNameFormatter | null = null;

  constructor() {
    super(inject(ChangeDetectorRef));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** True until both the current values and the dropdown lookups have arrived. */
  get isBusy(): boolean {
    return this.isLoading || this.isLoadingOptions;
  }

  get showsFormats(): boolean {
    return this.sections.includes('formats');
  }

  get showsLanguage(): boolean {
    return this.sections.includes('language');
  }

  onCancel(): void {
    if (!this.savedValue) {
      return;
    }

    this.resetForm(this.savedValue);
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.cdr.markForCheck();
  }

  protected buildForm(): FormGroup {
    return new FormGroup({
      dateFormat: new FormControl<DateDisplayFormat>(DateDisplayFormat.DayMonthYear, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      timeFormat: new FormControl<TimeDisplayFormat>(TimeDisplayFormat.TwentyFourHour, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      phoneNumberFormat: new FormControl<PhoneNumberDisplayFormat>(
        PhoneNumberDisplayFormat.International,
        { nonNullable: true, validators: [Validators.required] }
      ),
      numberFormat: new FormControl<NumberDisplayFormat>(
        NumberDisplayFormat.DotThousandCommaDecimal,
        { nonNullable: true, validators: [Validators.required] }
      ),
      language: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      timeZone: new FormControl<string | null>('Europe/Istanbul'),
      defaultCountryCode: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }

  protected override afterFormInit(): void {
    this.buildFormatOptions();
    this.loadOptions();
    this.loadSettings();
  }

  protected submitRequest(value: LocalizationSettingsDto): Observable<LocalizationSettingsDto> {
    this.disable();

    return this.localizationSettingsService.save(value).pipe(
      tap(serverSettings => {
        this.savedValue = serverSettings;
        this.form.reset(serverSettings);
        this.form.markAsPristine();
        this.form.markAsUntouched();
      }),
      finalize(() => this.enable())
    );
  }

  private buildFormatOptions(): void {
    this.dateFormatOptions = this.localizeOptions(DATE_FORMAT_OPTIONS);
    this.timeFormatOptions = this.localizeOptions(TIME_FORMAT_OPTIONS);
    this.phoneNumberFormatOptions = this.localizeOptions(PHONE_NUMBER_FORMAT_OPTIONS);
    this.numberFormatOptions = this.localizeOptions(NUMBER_FORMAT_OPTIONS);
  }

  private localizeOptions<TValue>(
    options: LocalizationFormatOption<TValue>[]
  ): SelectOption<TValue>[] {
    return options.map(option => ({
      value: option.value,
      label: this.localization.instant(option.labelKey),
    }));
  }

  private loadOptions(): void {
    forkJoin({
      options: this.localizationSettingsService.getOptions(),
      timeZones: this.localizationSettingsService.getTimeZones(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ options, timeZones }) => {
          this.languageOptions = options.languages ?? [];
          this.timeZoneOptions = timeZones;
          this.setCountryCallingCodes(options.countryCallingCodes ?? []);
          this.isLoadingOptions = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingOptions = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadSettings(): void {
    this.localizationSettingsService
      .get()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: settings => {
          this.savedValue = settings;
          this.resetForm(settings);
          this.form.markAsPristine();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private setCountryCallingCodes(codes: CountryCallingCodeOptionDto[]): void {
    const regionNames = this.getRegionNames();
    this.countryCodeOptions = codes.map(option => {
      const names = this.localizeRegionNames(option.regionCodes, regionNames);
      // The server's English fallback is one joined string (a name may itself contain a comma), so it is
      // shown whole; it only applies to the few regions a runtime cannot name.
      const regions = names ? this.shortenRegionNames(names) : option.displayName;
      return {
        value: option.code,
        label: this.localization.instant(
          'Settings::Localization:DefaultCountryCodeOption',
          option.code,
          regions
        ),
        searchText: names ? names.join(', ') : option.displayName,
      };
    });
  }

  private shortenRegionNames(names: readonly string[]): string {
    const listed = names.slice(0, MAX_LABELLED_REGIONS);
    if (names.length > MAX_LABELLED_REGIONS) {
      listed.push('…');
    }

    return listed.join(', ');
  }

  /**
   * Country names in the current UI language, in the server's order. Returns `null` when any region of the
   * option cannot be localized (e.g. `XK`, `AC`, `TA` on some runtimes), so the caller falls back to the
   * server's English names for the whole option instead of mixing names and raw codes.
   */
  private localizeRegionNames(
    regionCodes: readonly string[],
    regionNames: Intl.DisplayNames | null
  ): string[] | null {
    if (!regionNames || regionCodes.length === 0) {
      return null;
    }

    const names: string[] = [];
    for (const regionCode of regionCodes) {
      const name = this.tryRegionName(regionNames, regionCode);
      if (!name || name === regionCode) {
        return null;
      }
      names.push(name);
    }

    return names;
  }

  private tryRegionName(regionNames: Intl.DisplayNames, regionCode: string): string | undefined {
    try {
      return regionNames.of(regionCode);
    } catch {
      return undefined;
    }
  }

  private getRegionNames(): Intl.DisplayNames | null {
    const language = this.localization.currentLang ?? '';
    if (this.regionNames?.language !== language) {
      this.regionNames = { language, formatter: this.createRegionNames(language) };
    }

    return this.regionNames.formatter;
  }

  private createRegionNames(language: string): Intl.DisplayNames | null {
    try {
      return new Intl.DisplayNames(language ? [language] : [], { type: 'region', fallback: 'none' });
    } catch {
      return null;
    }
  }
}
